import { useMemo } from "react";

export interface UseFormCompletionOptions {
  excludeFields?: string[];
  requiredFields?: string[];
}

/**
 * Hook to calculate form completion percentage
 */
export const useFormCompletion = (
  values: Record<string, any>,
  options: UseFormCompletionOptions = {}
) => {
  const { excludeFields = [], requiredFields = [] } = options;

  const completionPercentage = useMemo(() => {
    const fieldEntries = Object.entries(values).filter(
      ([key]) => !excludeFields.includes(key)
    );

    if (fieldEntries.length === 0) return 0;

    const filledFields = fieldEntries.filter(([key, value]) => {
      // If it's a required field, it must have a meaningful value
      if (requiredFields.includes(key)) {
        if (typeof value === 'string') return value.trim() !== '';
        if (typeof value === 'number') return value !== null && value !== undefined;
        return value !== null && value !== undefined;
      }

      // For optional fields, any value (including false) counts as filled
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'boolean') return true; // Boolean fields are always "complete"
      if (typeof value === 'number') return value !== null && value !== undefined;
      return value !== null && value !== undefined;
    });

    return Math.round((filledFields.length / fieldEntries.length) * 100);
  }, [values, excludeFields, requiredFields]);

  return completionPercentage;
};
